import * as Icons from 'lucide-react';
import { ArrowRight, CheckCircle, Mail, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { resolveMediaUrl } from '../lib/api';
import { DEFAULT_PUBLIC_LOGO } from '../context/ClinicBrandingContext';
import AddressMapLink from './AddressMapLink';
import WhatsAppLink from './WhatsAppLink';
import { getAddressDisplayLabel } from '../lib/googleMaps';
import {
  DEFAULT_BRAND_NAME,
  DEFAULT_CLINIC_SECTION_IMAGE,
  DEFAULT_FOOTER_BLURB,
  DEFAULT_HERO_IMAGE,
  DEFAULT_HOME_FEATURES,
  DEFAULT_SERVICES_BULLETS,
  resolveHomeText,
} from '../lib/clinicSiteDefaults';

function iconFromApiName(name) {
  const pascal = String(name || 'shield')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join('');
  return Icons[pascal] || Icons.Shield;
}

function resolveLogo(url) {
  if (typeof url === 'string' && url.trim()) return resolveMediaUrl(url);
  return DEFAULT_PUBLIC_LOGO;
}

function shortTagline(siteTagline, address) {
  if (siteTagline && String(siteTagline).trim()) {
    const tag = siteTagline.trim();
    return getAddressDisplayLabel(tag) || tag;
  }
  const addr = getAddressDisplayLabel(address) || String(address || 'Cd Sahagún, Hgo.');
  return addr.length > 48 ? `${addr.slice(0, 45)}…` : addr;
}

export function PreviewFrame({ label, children, compact }) {
  return (
    <div className="rounded-2xl border border-border overflow-hidden bg-background shadow-sm" data-testid="site-preview-frame">
      <div className="px-4 py-2 bg-muted/60 border-b border-border text-xs font-medium text-muted-foreground">
        {label || 'Vista previa'}
      </div>
      <div className={compact ? 'p-3' : 'p-4'}>{children}</div>
    </div>
  );
}

export function BrandingPreview({
  name = DEFAULT_BRAND_NAME,
  logoUrl = '',
  phone = '',
  email = '',
  address = '',
  siteTaglineShort = '',
  footerBlurb = '',
  copyrightName = '',
  footerHoursWeekdays = '',
  footerHoursLunch = '',
  footerHoursWeekend = '',
}) {
  const brand = String(name || '').trim() || DEFAULT_BRAND_NAME;
  const logo = resolveLogo(logoUrl);
  const blurb = String(footerBlurb || '').trim() || DEFAULT_FOOTER_BLURB;
  const info = {
    name: brand,
    footer_hours_weekdays: footerHoursWeekdays,
    footer_hours_lunch: footerHoursLunch,
    footer_hours_weekend: footerHoursWeekend,
  };
  const hw = resolveHomeText('footer_hours_weekdays', info) || 'Lunes a Viernes: 9:00 AM - 5:00 PM';
  const hl = resolveHomeText('footer_hours_lunch', info) || 'Hora de comida: 2:00 PM - 3:00 PM';
  const hwe = resolveHomeText('footer_hours_weekend', info) || 'Sábado y Domingo: Cerrado';
  const copy = String(copyrightName || '').trim() || brand;
  const topRight = shortTagline(siteTaglineShort, address);

  return (
    <PreviewFrame label="Vista previa — cabecera y pie del sitio">
      <div className="space-y-4 text-sm">
        <div className="rounded-lg overflow-hidden border border-border">
          <div className="bg-primary text-primary-foreground px-3 py-2 flex flex-wrap justify-between gap-2 text-xs">
            <div className="flex gap-3 flex-wrap">
              <WhatsAppLink phone={phone} className="flex items-center gap-1 hover:underline">
                <Phone className="w-3 h-3" />
                {phone || '+52 …'}
              </WhatsAppLink>
              <span className="hidden sm:flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {email || 'correo@…'}
              </span>
            </div>
            <AddressMapLink address={address} className="flex items-center gap-1 max-w-[12rem] truncate hover:underline">
              <MapPin className="w-3 h-3 shrink-0" />
              {topRight}
            </AddressMapLink>
          </div>
          <div className="bg-white px-3 py-3 flex items-center gap-3 border-b border-border">
            <img src={logo} alt={brand} className="h-10 w-auto max-w-[140px] object-contain" />
            <span className="font-heading font-semibold text-foreground truncate">{brand}</span>
          </div>
        </div>

        <div className="rounded-lg overflow-hidden bg-primary text-primary-foreground p-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
            <div>
              <img src={logo} alt={brand} className="h-10 w-auto max-w-[120px] object-contain mb-2 brightness-0 invert" />
              <p className="text-primary-foreground/80 leading-relaxed">{blurb}</p>
            </div>
            <div>
              <p className="font-semibold mb-2">Horarios</p>
              <p className="text-primary-foreground/80">{hw}</p>
              <p className="text-primary-foreground/80">{hl}</p>
              <p className="text-primary-foreground/80">{hwe}</p>
            </div>
            <div>
              <p className="font-semibold mb-2">Contacto</p>
              <p className="text-primary-foreground/80">
                {phone ? (
                  <WhatsAppLink phone={phone} className="hover:text-primary-foreground hover:underline" />
                ) : (
                  '—'
                )}
              </p>
              <p className="text-primary-foreground/80">{email || '—'}</p>
              <p className="text-primary-foreground/80 mt-1">
                {address ? (
                  <AddressMapLink
                    address={address}
                    className="hover:text-primary-foreground underline-offset-2 hover:underline"
                  />
                ) : (
                  '—'
                )}
              </p>
            </div>
          </div>
          <p className="text-center text-primary-foreground/60 text-[10px] mt-4 pt-3 border-t border-primary-foreground/20">
            © {new Date().getFullYear()} {copy}. Todos los derechos reservados.
          </p>
        </div>
      </div>
    </PreviewFrame>
  );
}

export function HeroPreview({
  brandName = DEFAULT_BRAND_NAME,
  badge = '',
  titlePrefix = '',
  titleHighlight = '',
  subtitle = '',
  heroImageUrl = '',
}) {
  const brand = String(brandName || '').trim() || DEFAULT_BRAND_NAME;
  const info = { name: brand, hero_badge_text: badge, hero_title_prefix: titlePrefix, hero_title_highlight: titleHighlight, hero_subtitle: subtitle };
  const heroBadge = resolveHomeText('hero_badge_text', info);
  const heroPrefix = resolveHomeText('hero_title_prefix', info);
  const heroHighlight = resolveHomeText('hero_title_highlight', info);
  const heroSubtitle = resolveHomeText('hero_subtitle', info);
  const heroImg =
    (typeof heroImageUrl === 'string' && heroImageUrl.trim() ? resolveMediaUrl(heroImageUrl) : '') ||
    DEFAULT_HERO_IMAGE;

  return (
    <PreviewFrame label="Vista previa — hero (inicio)">
      <div className="rounded-xl overflow-hidden bg-gradient-to-br from-sage-50 to-background p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-medium">
              <CheckCircle className="w-3 h-3" />
              {heroBadge}
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold leading-tight">
              {heroPrefix} <span className="text-primary">{heroHighlight}</span>
            </h2>
            <p className="text-muted-foreground text-sm">{heroSubtitle}</p>
            <div className="flex gap-2 pointer-events-none opacity-80">
              <Button size="sm" className="rounded-full text-xs">Agendar Cita</Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs">
                Ver Servicios
              </Button>
            </div>
          </div>
          <div className="relative">
            <img src={heroImg} alt={brand} className="rounded-2xl w-full h-36 md:h-44 object-cover shadow-md" />
          </div>
        </div>
      </div>
    </PreviewFrame>
  );
}

export function FeaturesPreview({ featureRows = [] }) {
  const features =
    Array.isArray(featureRows) && featureRows.some((r) => String(r.title || '').trim())
      ? featureRows.filter((r) => String(r.title || '').trim() || String(r.desc || '').trim())
      : DEFAULT_HOME_FEATURES;

  return (
    <PreviewFrame label="Vista previa — tarjetas de valores">
      <div className="grid grid-cols-2 gap-3">
        {features.map((feature, index) => {
          const Cmp = iconFromApiName(feature.icon);
          return (
            <Card key={index} className="border border-border p-3 text-center">
              <CardContent className="p-0 space-y-2">
                <div className="w-9 h-9 mx-auto bg-primary/10 rounded-lg flex items-center justify-center">
                  <Cmp className="w-4 h-4 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-xs text-foreground">
                  {feature.title || '—'}
                </h3>
                <p className="text-[10px] text-muted-foreground leading-snug">{feature.desc || '—'}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </PreviewFrame>
  );
}

export function ServicesSectionPreview({
  title,
  intro,
  bulletsText = '',
  imageUrl = '',
  brandName = DEFAULT_BRAND_NAME,
}) {
  const info = { services_section_title: title, services_section_intro: intro };
  const servicesTitle = resolveHomeText('services_section_title', info);
  const servicesIntro = resolveHomeText('services_section_intro', info);
  const services = bulletsText
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const items = services.length > 0 ? services : DEFAULT_SERVICES_BULLETS;
  const clinicImg =
    (typeof imageUrl === 'string' && imageUrl.trim() ? resolveMediaUrl(imageUrl) : '') ||
    DEFAULT_CLINIC_SECTION_IMAGE;

  return (
    <PreviewFrame label="Vista previa — sección servicios">
      <div className="rounded-xl bg-sage-50/50 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground mb-2">{servicesTitle}</h2>
            <p className="text-muted-foreground text-xs mb-3">{servicesIntro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {items.map((service, index) => (
                <div
                  key={`${index}-${service}`}
                  className="flex items-center gap-2 bg-white p-2 rounded-lg border border-border text-xs"
                >
                  <CheckCircle className="w-4 h-4 text-success flex-shrink-0" />
                  <span>{service}</span>
                </div>
              ))}
            </div>
          </div>
          <img src={clinicImg} alt={brandName} className="rounded-2xl w-full h-36 object-cover" />
        </div>
      </div>
    </PreviewFrame>
  );
}

export function ProductsSectionPreview({
  featuredProductsTitle = '',
  featuredProductsIntro = '',
  brandName = DEFAULT_BRAND_NAME,
}) {
  const info = {
    name: brandName,
    featured_products_title: featuredProductsTitle,
    featured_products_intro: featuredProductsIntro,
  };
  const prodTitle = resolveHomeText('featured_products_title', info);
  const prodIntro = resolveHomeText('featured_products_intro', info);

  return (
    <PreviewFrame label="Vista previa — productos destacados (home)">
      <div className="text-center p-3 bg-white rounded-lg border border-border text-sm">
        <h3 className="font-heading font-bold text-base mb-1">{prodTitle}</h3>
        <p className="text-muted-foreground text-xs">{prodIntro}</p>
        <p className="text-[10px] text-muted-foreground mt-2 italic">
          Las tarjetas de producto se cargan desde Admin → Productos.
        </p>
      </div>
    </PreviewFrame>
  );
}

export function ReviewsSectionPreview({ reviewsSectionTitle = '', brandName = DEFAULT_BRAND_NAME }) {
  const info = { name: brandName, reviews_section_title: reviewsSectionTitle };
  const reviewsTitle = resolveHomeText('reviews_section_title', info);

  return (
    <PreviewFrame label="Vista previa — reseñas (home)">
      <div className="text-center p-3 bg-sage-50/50 rounded-lg border border-border text-sm">
        <h3 className="font-heading font-bold text-base">{reviewsTitle}</h3>
        <p className="text-[10px] text-muted-foreground mt-2 italic">
          Las opiniones aprobadas aparecen aquí (Admin → Reseñas).
        </p>
      </div>
    </PreviewFrame>
  );
}

export function CtaSectionPreview({
  ctaTitle = '',
  ctaSubtitle = '',
  brandName = DEFAULT_BRAND_NAME,
}) {
  const info = { name: brandName, cta_title: ctaTitle, cta_subtitle: ctaSubtitle };
  const ctaT = resolveHomeText('cta_title', info);
  const ctaS = resolveHomeText('cta_subtitle', info);

  return (
    <PreviewFrame label="Vista previa — llamada a la acción (franja verde)">
      <div className="text-center p-4 bg-primary rounded-lg text-primary-foreground text-sm">
        <h3 className="font-heading font-bold text-base mb-1">{ctaT}</h3>
        <p className="text-primary-foreground/80 text-xs mb-3">{ctaS}</p>
        <Button
          size="sm"
          variant="secondary"
          className="rounded-full text-xs bg-white text-primary pointer-events-none"
          type="button"
          tabIndex={-1}
        >
          Agendar Cita Ahora
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
    </PreviewFrame>
  );
}

export function ContactHomePreview({ contactSectionTitle = '', brandName = DEFAULT_BRAND_NAME }) {
  const info = { name: brandName, contact_section_title: contactSectionTitle };
  const contactTitle = resolveHomeText('contact_section_title', info);

  return (
    <PreviewFrame label="Vista previa — título bloque contacto (home)">
      <div className="text-center p-3 bg-white rounded-lg border border-border text-sm">
        <h3 className="font-heading font-bold text-base">{contactTitle}</h3>
        <p className="text-[10px] text-muted-foreground mt-2">
          Teléfono, correo y dirección: sección «Marca y contacto». Horarios: sección «Horarios».
        </p>
      </div>
    </PreviewFrame>
  );
}

function ProductsSectionPreviewBlock({ featuredProductsTitle, featuredProductsIntro, brandName }) {
  const info = {
    name: brandName || DEFAULT_BRAND_NAME,
    featured_products_title: featuredProductsTitle,
    featured_products_intro: featuredProductsIntro,
  };
  const prodTitle = resolveHomeText('featured_products_title', info);
  const prodIntro = resolveHomeText('featured_products_intro', info);
  return (
    <div className="text-center p-3 bg-white rounded-lg border border-border">
      <h3 className="font-heading font-bold text-base mb-1">{prodTitle}</h3>
      <p className="text-muted-foreground text-xs">{prodIntro}</p>
      <p className="text-[10px] text-muted-foreground mt-2 italic">
        Las tarjetas de producto se cargan desde Admin → Productos.
      </p>
    </div>
  );
}

function ReviewsSectionPreviewBlock({ reviewsSectionTitle, brandName }) {
  const info = { name: brandName || DEFAULT_BRAND_NAME, reviews_section_title: reviewsSectionTitle };
  const reviewsTitle = resolveHomeText('reviews_section_title', info);
  return (
    <div className="text-center p-3 bg-sage-50/50 rounded-lg border border-border">
      <h3 className="font-heading font-bold text-base">{reviewsTitle}</h3>
      <p className="text-[10px] text-muted-foreground mt-2 italic">
        Las opiniones aprobadas aparecen aquí (Admin → Reseñas).
      </p>
    </div>
  );
}

function CtaSectionPreviewBlock({ ctaTitle, ctaSubtitle, brandName }) {
  const info = { name: brandName || DEFAULT_BRAND_NAME, cta_title: ctaTitle, cta_subtitle: ctaSubtitle };
  const ctaT = resolveHomeText('cta_title', info);
  const ctaS = resolveHomeText('cta_subtitle', info);
  return (
    <div className="text-center p-4 bg-primary rounded-lg text-primary-foreground">
      <h3 className="font-heading font-bold text-base mb-1">{ctaT}</h3>
      <p className="text-primary-foreground/80 text-xs mb-3">{ctaS}</p>
      <Button
        size="sm"
        variant="secondary"
        className="rounded-full text-xs bg-white text-primary pointer-events-none"
        type="button"
        tabIndex={-1}
      >
        Agendar Cita Ahora
        <ArrowRight className="w-3 h-3 ml-1" />
      </Button>
    </div>
  );
}

function ContactHomePreviewBlock({ contactSectionTitle, brandName }) {
  const info = { name: brandName || DEFAULT_BRAND_NAME, contact_section_title: contactSectionTitle };
  const contactTitle = resolveHomeText('contact_section_title', info);
  return (
    <div className="text-center p-3 bg-white rounded-lg border border-border">
      <h3 className="font-heading font-bold text-base">{contactTitle}</h3>
      <p className="text-[10px] text-muted-foreground mt-2">
        Teléfono, correo y dirección: sección «Marca y contacto». Horarios: sección «Horarios».
      </p>
    </div>
  );
}

/** Vista previa combinada (compatibilidad). */
export function LowerSectionsPreview({
  featuredProductsTitle = '',
  featuredProductsIntro = '',
  reviewsSectionTitle = '',
  ctaTitle = '',
  ctaSubtitle = '',
  contactSectionTitle = '',
  brandName = DEFAULT_BRAND_NAME,
}) {
  return (
    <PreviewFrame label="Vista previa — secciones inferiores del home">
      <div className="space-y-4 text-sm">
        <ProductsSectionPreviewBlock
          featuredProductsTitle={featuredProductsTitle}
          featuredProductsIntro={featuredProductsIntro}
          brandName={brandName}
        />
        <ReviewsSectionPreviewBlock reviewsSectionTitle={reviewsSectionTitle} brandName={brandName} />
        <CtaSectionPreviewBlock ctaTitle={ctaTitle} ctaSubtitle={ctaSubtitle} brandName={brandName} />
        <ContactHomePreviewBlock contactSectionTitle={contactSectionTitle} brandName={brandName} />
      </div>
    </PreviewFrame>
  );
}

export function HoursPreview({
  footerHoursWeekdays = '',
  footerHoursLunch = '',
  footerHoursWeekend = '',
  address = '',
  phone = '',
  email = '',
  contactSectionTitle = '',
  brandName = DEFAULT_BRAND_NAME,
}) {
  const info = {
    name: brandName,
    contact_section_title: contactSectionTitle,
    footer_hours_weekdays: footerHoursWeekdays,
    footer_hours_lunch: footerHoursLunch,
    footer_hours_weekend: footerHoursWeekend,
  };
  const contactTitle = resolveHomeText('contact_section_title', info);
  const hw = resolveHomeText('footer_hours_weekdays', info);
  const hl = resolveHomeText('footer_hours_lunch', info);
  const hwe = resolveHomeText('footer_hours_weekend', info);

  return (
    <PreviewFrame label="Vista previa — contacto y horarios (bloque del home)">
      <div className="space-y-3">
        <h3 className="font-heading font-bold text-center text-base">{contactTitle}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card className="p-3 border border-border">
            <CardContent className="p-0 space-y-2 text-xs">
              <p className="font-semibold">Información de Contacto</p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Dirección:</strong>{' '}
                {address ? (
                  <AddressMapLink
                    address={address}
                    className="text-primary hover:underline underline-offset-2"
                  />
                ) : (
                  '—'
                )}
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Teléfono:</strong>{' '}
                {phone ? (
                  <WhatsAppLink
                    phone={phone}
                    className="text-primary hover:underline underline-offset-2"
                  />
                ) : (
                  '—'
                )}
              </p>
              <p className="text-muted-foreground">
                <strong className="text-foreground">Email:</strong> {email || '—'}
              </p>
            </CardContent>
          </Card>
          <Card className="p-3 border border-border">
            <CardContent className="p-0 space-y-2 text-xs">
              <p className="font-semibold">Horarios de Atención</p>
              <p className="text-muted-foreground">{hw}</p>
              <p className="text-muted-foreground">{hl}</p>
              <p className="text-muted-foreground">{hwe}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </PreviewFrame>
  );
}

/** Vista previa compacta de toda la página principal (Configura Sitio). */
export function HomePagePreview({
  draft,
  featureRows = DEFAULT_HOME_FEATURES,
  servicesBullets = DEFAULT_SERVICES_BULLETS,
}) {
  if (!draft) return null;
  const brandName = draft.name || DEFAULT_BRAND_NAME;
  const bullets =
    Array.isArray(servicesBullets) && servicesBullets.length > 0
      ? servicesBullets
      : DEFAULT_SERVICES_BULLETS;
  const features =
    Array.isArray(featureRows) && featureRows.length > 0 ? featureRows : DEFAULT_HOME_FEATURES;

  return (
    <PreviewFrame label="Vista previa — página principal completa">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
        <BrandingPreview
          name={draft.name}
          logoUrl={draft.logo_url}
          phone={draft.phone}
          email={draft.email}
          address={draft.address}
          siteTaglineShort={draft.site_tagline_short}
          footerBlurb={draft.footer_blurb}
          copyrightName={draft.copyright_name}
          footerHoursWeekdays={draft.footer_hours_weekdays}
          footerHoursLunch={draft.footer_hours_lunch}
          footerHoursWeekend={draft.footer_hours_weekend}
        />
        <HeroPreview
          badge={draft.hero_badge_text}
          titlePrefix={draft.hero_title_prefix}
          titleHighlight={draft.hero_title_highlight}
          subtitle={draft.hero_subtitle}
          heroImageUrl={draft.hero_image_url}
          brandName={brandName}
        />
        <FeaturesPreview featureRows={features} />
        <ServicesSectionPreview
          title={draft.services_section_title}
          intro={draft.services_section_intro}
          bulletsText={Array.isArray(servicesBullets) ? servicesBullets.join('\n') : String(servicesBullets || '')}
          imageUrl={draft.clinic_section_image_url}
          brandName={brandName}
        />
        <LowerSectionsPreview
          featuredProductsTitle={draft.featured_products_title}
          featuredProductsIntro={draft.featured_products_intro}
          reviewsSectionTitle={draft.reviews_section_title}
          ctaTitle={draft.cta_title}
          ctaSubtitle={draft.cta_subtitle}
          contactSectionTitle={draft.contact_section_title}
          brandName={brandName}
        />
        <HoursPreview
          footerHoursWeekdays={draft.footer_hours_weekdays}
          footerHoursLunch={draft.footer_hours_lunch}
          footerHoursWeekend={draft.footer_hours_weekend}
          address={draft.address}
          phone={draft.phone}
          email={draft.email}
          contactSectionTitle={draft.contact_section_title}
          brandName={brandName}
        />
      </div>
    </PreviewFrame>
  );
}
